import React, { useState } from "react";
import { Navigation, ChevronDown } from "lucide-react";

/**
 * Price — Bouton "Ouvrir dans Maps" pour le covoitureur.
 *
 * Approche déléguée (option 2a) : on n'implémente PAS de nav in-app,
 * on ouvre simplement l'app native de navigation choisie par le user
 * (Plans iOS, Google Maps, Waze) via URL scheme.
 *
 *   Plans iOS       : maps://?daddr=lat,lng&dirflg=d
 *   Google Maps iOS : comgooglemaps://?daddr=lat,lng&directionsmode=driving
 *   Google Maps web : https://www.google.com/maps/dir/?api=1&destination=lat,lng
 *   Waze            : https://waze.com/ul?ll=lat,lng&navigate=yes
 *   Android geo:    : geo:lat,lng?q=lat,lng(Name)
 *
 * Sur iOS, si l'app n'est pas installée, le scheme échoue silencieusement
 * → on fallback sur Google Maps web (universel).
 *
 * Props :
 *   - lat, lng   : coordonnées destination (obligatoire)
 *   - label      : nom du lieu (optionnel, affiché sur Waze/Plans)
 *   - variant    : "compact" (mini bouton) | "full" (bouton large)
 *   - testid     : data-testid override
 */
export default function NavButtons({ lat, lng, label = "", variant = "full", testid = "nav-buttons" }) {
  const [open, setOpen] = useState(false);

  if (lat == null || lng == null) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const encoded = encodeURIComponent(label);

  const openScheme = (scheme, fallback) => {
    setOpen(false);
    // Try the app scheme first. On iOS if the app isn't installed, nothing
    // happens (the page stays put), so we set a short timer to fall back
    // to the universal URL. Skipping the fallback on Android since geo:
    // intents typically resolve to a chooser.
    const before = Date.now();
    window.location.href = scheme;
    if (fallback && isIOS) {
      setTimeout(() => {
        // If the user is still on our page after 700 ms → scheme failed.
        if (document.visibilityState !== "hidden" && Date.now() - before < 1500) {
          window.location.href = fallback;
        }
      }, 700);
    }
  };

  const openGoogleMaps = () => {
    const scheme = `comgooglemaps://?daddr=${lat},${lng}&directionsmode=driving`;
    const web = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    openScheme(isIOS ? scheme : web, web);
  };

  const openAppleMaps = () => {
    openScheme(`maps://?daddr=${lat},${lng}&dirflg=d${label ? `&q=${encoded}` : ""}`,
      `https://maps.apple.com/?daddr=${lat},${lng}`);
  };

  const openWaze = () => {
    openScheme(`waze://?ll=${lat},${lng}&navigate=yes`,
      `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`);
  };

  const openDefault = () => {
    // Best default per-OS : Plans on iOS (no install friction), Google Maps
    // web on everything else (opens native app if installed on Android).
    if (isIOS) openAppleMaps();
    else openGoogleMaps();
  };

  const compact = variant === "compact";

  return (
    <div className="relative inline-block" data-testid={testid}>
      <div className={"flex items-center " + (compact ? "gap-1" : "gap-1.5")}>
        <button
          type="button"
          onClick={openDefault}
          data-testid={`${testid}-launch`}
          className={
            "street-press inline-flex items-center gap-1.5 rounded-2xl bg-primary text-primary-foreground font-[Outfit] font-black street-border-thin street-shadow-sm " +
            (compact ? "h-9 px-3 text-[12px]" : "h-11 px-4 text-[13px]")
          }
        >
          <Navigation className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} strokeWidth={2.5} />
          {compact ? "GPS" : "Lancer le GPS"}
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`${testid}-menu`}
          aria-label="Choisir l'app de navigation"
          className={
            "street-press inline-flex items-center justify-center rounded-2xl bg-white street-border-thin street-shadow-sm " +
            (compact ? "h-9 w-9" : "h-11 w-11")
          }
        >
          <ChevronDown className={compact ? "h-4 w-4" : "h-4 w-4"} strokeWidth={2.5} />
        </button>
      </div>

      {open && (
        <>
          <div className="fixed inset-0 z-[75]" onClick={() => setOpen(false)} />
          <div
            className="absolute z-[76] right-0 mt-2 w-52 rounded-2xl bg-white border-2 border-black shadow-[0_4px_0_0_#000] overflow-hidden"
            data-testid={`${testid}-menu-list`}
          >
            <button
              type="button"
              onClick={openAppleMaps}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-neutral-100 font-[Outfit] text-[13px] font-bold border-b border-neutral-200"
              data-testid={`${testid}-plans`}
            >
              <span className="w-7 h-7 rounded-lg bg-[#5AC8FA] border border-black grid place-items-center text-white font-black text-[10px]">P</span>
              Plans (iOS)
            </button>
            <button
              type="button"
              onClick={openGoogleMaps}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-neutral-100 font-[Outfit] text-[13px] font-bold border-b border-neutral-200"
              data-testid={`${testid}-gmaps`}
            >
              <span className="w-7 h-7 rounded-lg bg-[#34A853] border border-black grid place-items-center text-white font-black text-[10px]">G</span>
              Google Maps
            </button>
            <button
              type="button"
              onClick={openWaze}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-neutral-100 font-[Outfit] text-[13px] font-bold"
              data-testid={`${testid}-waze`}
            >
              <span className="w-7 h-7 rounded-lg bg-[#33CCFF] border border-black grid place-items-center text-white font-black text-[10px]">W</span>
              Waze
            </button>
          </div>
        </>
      )}
    </div>
  );
}
